import React, { useCallback } from 'react';
import { View } from 'react-native';

import { useThemedStyles } from '../../../hooks/useThemedStyles';
import { Text } from '../../../components';
import { InformationCallout } from '../../../components/callout';
import { TenantConstantSelector, CommDeviceSelector } from '../../../components/selector';
import SetupSection from '../components/SetupSection';
import SetupInfoRow from '../components/SetupInfoRow';
import SetupSwitchField from '../components/SetupSwitchField';
import SetupPickerField from '../components/SetupPickerField';
import SetupTextField from '../components/SetupTextField';
import SetupPhotoSection from '../components/SetupPhotoSection';
import SetupDateField from '../components/SetupDateField';
import { PlanogramDetail } from '../../planogram';

import { nativePlanogramVersions } from '../../../../common/utils/vending-settings';
import { sortPickListItems } from '../../../utils/sortMethods';
import { hasMachineSetupPaymentDevices } from '../../../../common/utils/machine/machineSetupPayments';

import {
	MACHINE_SETUP_BRANDING_VALUE,
	MACHINE_SETUP_BRANDING_VALUE_NAME,
	MACHINE_SETUP_CREDIT_CARD_READER_TYPE,
	MACHINE_SETUP_CREDIT_CARD_READER_TYPE_NAME,
	TENANT_CONSTANT_TYPE,
	MACHINE_BRAND,
	MACHINE_BRAND_NAME,
	POS_TYPE,
} from '../../../../common/constants';
import {
	cashPeripheralSteps,
	finalSteps,
	getIdOrNull,
	planogramSteps,
	preliminarySteps,
} from './MachineSetupDetailHelpers';
import { createMachineSetupDetailStyles } from './MachineSetupDetail.styles';

const MachineSetupPhysicalSection = ({
	machineSetup,
	assignedMachine,
	isDriver,
	cardPaymentOnlyDisplay,
	updateMachineSetup,
	addPhoto,
	handleUpdateMediaComment,
	handlePaymentDeviceSerialUpdate,
}) => {
	const styles = useThemedStyles(createMachineSetupDetailStyles);
	const hasPaymentDevices = hasMachineSetupPaymentDevices(machineSetup);
	// Treat as a move when we can resolve a previous non-warehouse location and it differs from the setup location.
	const machineLocation = assignedMachine?.location || null;
	const machinePreviousLocation = assignedMachine?.previousLocation || null;
	const machinePlaceLocation = assignedMachine?.place?.location || null;
	const previousLocation = (
		(!machineLocation?.isWarehouse && machineLocation) ||
		(!machinePreviousLocation?.isWarehouse && machinePreviousLocation) ||
		(!machinePlaceLocation?.isWarehouse && machinePlaceLocation) ||
		machineLocation ||
		machinePreviousLocation ||
		machinePlaceLocation ||
		null
	);
	const nextLocation = machineSetup.locationSetup?.location || null;
	const isMoveSetup = Boolean(previousLocation?.id && nextLocation?.id && previousLocation.id !== nextLocation.id && !previousLocation.isWarehouse);
	const planogram = machineSetup.planogram || null;
	const planogramItems = planogram?.items || [];
	const machineSetupPlanogramIsMarket = assignedMachine?.type === POS_TYPE.MARKET;

	const paymentStepsExplanation = `Only the installation and testing steps for the stored payment requirements are shown.\n\nCard payment only machine? ${cardPaymentOnlyDisplay}`;
	const planogramCalloutText = machineSetup.planogram?.name
		? `Use ${machineSetup.planogram.name} when setting up coils, selection numbers, and prices.`
		: 'No required planogram was provided for this machine setup.';
	const paymentDevicesHint = (() => {
		const steps = [];
		if (machineSetup.requiresCashPayments) {
			steps.push('Test coins by purchasing something with a few coins.');
			steps.push('Test notes by purchasing something with notes. If any new bank bills are in circulation, test the new bank bills.');
		}
		if (machineSetup.requiresCreditCardReader) {
			steps.push('Test the credit card reader by purchasing something with a credit card.');
		}
		return `Test the following payment methods:\n${steps.map(item => `• ${item}`).join('\n')}`;
	})();
	const creditCardReaderCollectionHint = 'Collect the following information depending on the device type:\n• CPI 5-in-1 bezel: record the Phys ID\n• Nayax AMIT: record the 6 digit AMIT number\n• Nayax VPOS Touch: record the full (long) serial number as shown on the screen';

	const handleMachineKeyChange = useCallback(item => updateMachineSetup({ machineKey: getIdOrNull(item) }), [updateMachineSetup]);
	const handleMachineDistributorChange = useCallback(item => updateMachineSetup({ machineDistributor: getIdOrNull(item) }), [updateMachineSetup]);
	const handleMachineModelChange = useCallback(item => updateMachineSetup({ machineModel: getIdOrNull(item) }), [updateMachineSetup]);
	const handleMachineTypeChange = useCallback(item => updateMachineSetup({ machineType: getIdOrNull(item) }), [updateMachineSetup]);
	const handleMachineWrapChange = useCallback(item => updateMachineSetup({ machineWrap: getIdOrNull(item) }), [updateMachineSetup]);
	const handleCoinMechChange = useCallback(item => updateMachineSetup({ machineCoinMech: getIdOrNull(item) }), [updateMachineSetup]);
	const handleNoteReaderChange = useCallback(item => updateMachineSetup({ machineNoteReader: getIdOrNull(item) }), [updateMachineSetup]);
	const handleCommDeviceChange = useCallback(device => updateMachineSetup({ commDevice: getIdOrNull(device) }), [updateMachineSetup]);

	return (
		<SetupSection title="Physical setup">
			<Text style={styles.sectionLabel}>Preliminary setup steps</Text>
			{
				preliminarySteps.map(step => (
					<SetupSwitchField
						key={step.field}
						label={step.label}
						hint={step.hint}
						value={machineSetup[step.field]}
						onChange={value => updateMachineSetup({ [step.field]: value })}
						required={step.required}
					/>
				))
			}

			<Text style={styles.sectionLabel}>Payment system steps</Text>
			<InformationCallout text={paymentStepsExplanation} />
			{
				machineSetup.requiresCashPayments && (
					cashPeripheralSteps.map(step => (
						<SetupSwitchField
							key={step.field}
							label={step.label}
							hint={step.hint}
							value={machineSetup[step.field]}
							onChange={value => updateMachineSetup({ [step.field]: value })}
							required={step.required}
						/>
					))
				)
			}
			{
				machineSetup.requiresCreditCardReader && (
					<SetupSwitchField
						label="Install credit card reader"
						value={machineSetup.installCreditCardReader}
						onChange={value => updateMachineSetup({ installCreditCardReader: value })}
						required={true}
					/>
				)
			}

			<Text style={styles.sectionLabel}>Planogram-related steps</Text>
			<InformationCallout text={planogramCalloutText} />
			{
				planogram?.id && (
					<View style={styles.planogramPreview}>
						<Text style={styles.subheadingSmall}>{planogram.name}</Text>
						{
							planogramItems.length ? (
								<PlanogramDetail
									items={planogramItems}
									version={nativePlanogramVersions.MACHINE_SETUP_VIEW}
									sortFunction={sortPickListItems}
									defaultHideImages={false}
									defaultHideHealthCompliance={false}
									isMarket={machineSetupPlanogramIsMarket}
									planogramItemGroups={planogram.planogramItemGroups}
								/>
							) : (
								<Text style={styles.fieldHint}>No products are set on this planogram.</Text>
							)
						}
					</View>
				)
			}
			{
				planogramSteps.map(step => (
					<SetupSwitchField
						key={step.field}
						label={step.label}
						hint={step.hint}
						value={machineSetup[step.field]}
						onChange={value => updateMachineSetup({ [step.field]: value })}
						required={step.required}
					/>
				))
			}

			<Text style={styles.sectionLabel}>Final setup steps</Text>
			{finalSteps.filter(step => (
				(!step.moveOnly || isMoveSetup) && (!step.paymentDevicesOnly || hasPaymentDevices)
			)).map(step => (
				<SetupSwitchField
					key={step.field}
					label={step.label}
					hint={step.field === 'testAllPaymentDevices' ? paymentDevicesHint : step.hint}
					value={machineSetup[step.field]}
					onChange={value => updateMachineSetup({ [step.field]: value })}
					required={step.required}
				/>
			))}
			<SetupPickerField
				label="Load branding onto machine screen"
				value={machineSetup.loadBranding}
				onChange={value => updateMachineSetup({ loadBranding: value })}
				options={Object.values(MACHINE_SETUP_BRANDING_VALUE).map(value => ({
					label: MACHINE_SETUP_BRANDING_VALUE_NAME[value],
					value,
				}))}
				required={true}
				hint="Load company branding (Vendy, AVC, MVC, etc.) if the machine has a screen. Select 'Not applicable (no screen)' otherwise."
			/>
			<SetupPhotoSection
				title="Upload a photo"
				photos={machineSetup.finalPhotos || []}
				onAddPhoto={(link) => addPhoto({ link, targetField: 'finalPhotos' })}
				onUpdateComment={handleUpdateMediaComment}
				addButtonLabel="Upload photo"
			/>
			<Text style={styles.photoHint}>Upload a photo of the fully-setup machine.</Text>

			<Text style={styles.sectionLabel}>Information collection steps</Text>
			<SetupTextField
				label="Machine serial number"
				value={machineSetup.machineSerialNumber}
				field="machineSerialNumber"
				onConfirm={updateMachineSetup}
				required={true}
			/>
			<Text style={styles.fieldLabel}>
				Machine key
				<Text style={styles.requiredAsterisk}> *</Text>
			</Text>
			<TenantConstantSelector
				initialValue={machineSetup.machineKey}
				value={machineSetup.machineKey}
				constantType={TENANT_CONSTANT_TYPE.MACHINE_KEY}
				onChange={handleMachineKeyChange}
			/>
			<Text style={styles.fieldLabel}>
				Machine distributor
				<Text style={styles.requiredAsterisk}> *</Text>
			</Text>
			<TenantConstantSelector
				initialValue={machineSetup.machineDistributor}
				value={machineSetup.machineDistributor}
				constantType={TENANT_CONSTANT_TYPE.MACHINE_DISTRIBUTOR}
				onChange={handleMachineDistributorChange}
			/>
			<SetupDateField
				label="Build date"
				value={machineSetup.buildDate}
				onChange={(date) => updateMachineSetup({ buildDate: date })}
				required={true}
			/>
			<Text style={styles.fieldLabel}>
				Machine model
				<Text style={styles.requiredAsterisk}> *</Text>
			</Text>
			<TenantConstantSelector
				initialValue={machineSetup.machineModel}
				value={machineSetup.machineModel}
				constantType={TENANT_CONSTANT_TYPE.MACHINE_MODEL}
				onChange={handleMachineModelChange}
			/>
			<SetupPickerField
				label="Machine brand"
				value={machineSetup.machineBrand}
				onChange={value => updateMachineSetup({ machineBrand: value })}
				options={Object.values(MACHINE_BRAND).map(value => ({
					label: MACHINE_BRAND_NAME[value],
					value,
				}))}
				required={true}
			/>
			<Text style={styles.fieldLabel}>
				Machine type
				<Text style={styles.requiredAsterisk}> *</Text>
			</Text>
			<TenantConstantSelector
				initialValue={machineSetup.machineType}
				value={machineSetup.machineType}
				constantType={TENANT_CONSTANT_TYPE.MACHINE_TYPE}
				onChange={handleMachineTypeChange}
			/>
			<Text style={styles.fieldLabel}>
				Machine wrap
				<Text style={styles.requiredAsterisk}> *</Text>
			</Text>
			<TenantConstantSelector
				initialValue={machineSetup.machineWrap}
				value={machineSetup.machineWrap}
				constantType={TENANT_CONSTANT_TYPE.MACHINE_WRAP}
				onChange={handleMachineWrapChange}
			/>
			{
				machineSetup.requiresCashPayments && (
					<>
						<Text style={styles.fieldLabel}>
							Coin mech type
							<Text style={styles.requiredAsterisk}> *</Text>
						</Text>
						<TenantConstantSelector
							initialValue={machineSetup.machineCoinMech}
							value={machineSetup.machineCoinMech}
							constantType={TENANT_CONSTANT_TYPE.MACHINE_COIN_MECH}
							onChange={handleCoinMechChange}
						/>
						<Text style={styles.fieldLabel}>
							Note reader type
							<Text style={styles.requiredAsterisk}> *</Text>
						</Text>
						<TenantConstantSelector
							initialValue={machineSetup.machineNoteReader}
							value={machineSetup.machineNoteReader}
							constantType={TENANT_CONSTANT_TYPE.MACHINE_NOTE_READER}
							onChange={handleNoteReaderChange}
						/>
					</>
				)
			}
			{
				machineSetup.requiresCreditCardReader && (
					<SetupPickerField
						label="Credit card reader type"
						value={machineSetup.creditCardReaderType}
						onChange={value => updateMachineSetup({ creditCardReaderType: value })}
						options={Object.values(MACHINE_SETUP_CREDIT_CARD_READER_TYPE).map(value => ({
							label: MACHINE_SETUP_CREDIT_CARD_READER_TYPE_NAME[value],
							value,
						}))}
						required={true}
					/>
				)
			}
			{isDriver ? (
				<SetupInfoRow
					label="Comm device"
					value={machineSetup.commDevice?.deviceId || 'Not set'}
				/>
			) : (
				<>
					<Text style={styles.fieldLabel}>
						Comm device
						<Text style={styles.requiredAsterisk}> *</Text>
					</Text>
					<CommDeviceSelector
						initialValue={machineSetup.commDevice}
						value={machineSetup.commDevice}
						onChange={handleCommDeviceChange}
						initialFilter={{ deleted: false }}
						selectablePointOfSale={assignedMachine}
						showAllocationContext={true}
						disabled={!assignedMachine}
					/>
					{
						!assignedMachine && (
							<Text style={styles.fieldHint}>To select the comm device, first select the machine.</Text>
						)
					}
				</>
			)}
			{
				machineSetup.requiresCreditCardReader && (
					<SetupTextField
						label="Credit card reader"
						value={machineSetup.paymentDeviceSerialNo}
						field="paymentDeviceSerialNo"
						onConfirm={handlePaymentDeviceSerialUpdate}
						placeholder="Enter serial number"
						disabled={!machineSetup.commDevice?.id}
						hint={!machineSetup.commDevice?.id ? 'To set up the credit card reader, first select the communication device.' : creditCardReaderCollectionHint}
						required={true}
					/>
				)
			}
			<SetupSwitchField
				label="Remove old directions & notes"
				value={machineSetup.removeOldDirectionsAndNotes}
				onChange={value => updateMachineSetup({ removeOldDirectionsAndNotes: value })}
				hint="Check if any old directions and notes exist, and remove them."
			/>
			<SetupSwitchField
				label="Update all machine information in Sentry"
				value={machineSetup.updateAllMachineInformation}
				onChange={value => updateMachineSetup({ updateAllMachineInformation: value })}
				hint="Whether the machine is new or not, re-input the data to make sure it is up-to-date."
			/>
		</SetupSection>
	);
};

export default MachineSetupPhysicalSection;
